import { useRef, useState, useCallback, useEffect } from 'react';
import { Camera, ImagePlus, Trash2, Loader2, X, Link } from 'lucide-react';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { cn } from '@paulownia/shared/lib/utils';
import {
  isValidImageFormat,
  processCroppedImage,
  IMAGE_ERROR_MESSAGES,
  type ProcessedImages,
  type ImageUploadErrorType,
} from '@paulownia/shared';
import { ImageCropper } from '../ImageCropper';

interface WizardImagePickerProps {
  /** Pending processed images (ready to upload after product creation) */
  pendingImage: ProcessedImages | null;
  /** Legacy URL field for external images */
  pictureUrl?: string;
  /** Called when an image has been selected and processed */
  onImageProcessed: (images: ProcessedImages) => void;
  /** Called when the image is removed */
  onImageRemoved: () => void;
  /** Called when the picture URL changes */
  onPictureUrlChange?: (url: string | undefined) => void;
  disabled?: boolean;
}

type Status = 'idle' | 'cropping' | 'processing' | 'error';

export function WizardImagePicker({
  pendingImage,
  pictureUrl,
  onImageProcessed,
  onImageRemoved,
  onPictureUrlChange,
  disabled = false,
}: WizardImagePickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState(pictureUrl || '');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [croppingImageUrl, setCroppingImageUrl] = useState<string | null>(null);

  // Create preview URL from pending image
  useEffect(() => {
    if (pendingImage) {
      const url = URL.createObjectURL(pendingImage.fullBlob);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [pendingImage]);

  // Update URL input when pictureUrl changes
  useEffect(() => {
    setUrlInput(pictureUrl || '');
  }, [pictureUrl]);

  const handleError = useCallback((errorType: ImageUploadErrorType) => {
    setStatus('error');
    setError(IMAGE_ERROR_MESSAGES[errorType]);
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be selected again
    e.target.value = '';

    // Validate format
    if (!isValidImageFormat(file)) {
      handleError('INVALID_FORMAT');
      return;
    }

    // Create preview URL for cropping
    const url = URL.createObjectURL(file);
    setCroppingImageUrl(url);
    setStatus('cropping');
    setError(null);
  }, [handleError]);

  const handleCropComplete = useCallback(async (croppedBlob: Blob) => {
    // Clean up cropping URL
    if (croppingImageUrl) {
      URL.revokeObjectURL(croppingImageUrl);
      setCroppingImageUrl(null);
    }

    setStatus('processing');

    try {
      const processedImages = await processCroppedImage(croppedBlob);
      setStatus('idle');
      onImageProcessed(processedImages);
      // Clear any picture URL since we now have a processed image
      if (onPictureUrlChange) {
        onPictureUrlChange(undefined);
      }
    } catch {
      handleError('PROCESSING_FAILED');
    }
  }, [croppingImageUrl, onImageProcessed, onPictureUrlChange, handleError]);

  const handleCropCancel = useCallback(() => {
    if (croppingImageUrl) {
      URL.revokeObjectURL(croppingImageUrl);
      setCroppingImageUrl(null);
    }
    setStatus('idle');
    setError(null);
  }, [croppingImageUrl]);

  const handleDelete = useCallback(() => {
    onImageRemoved();
    if (onPictureUrlChange) {
      onPictureUrlChange(undefined);
    }
    setUrlInput('');
    setStatus('idle');
    setError(null);
  }, [onImageRemoved, onPictureUrlChange]);

  const handleUrlSubmit = useCallback(() => {
    if (urlInput.trim() && onPictureUrlChange) {
      onPictureUrlChange(urlInput.trim());
      // Clear any pending image since we're using URL
      onImageRemoved();
      setShowUrlInput(false);
    }
  }, [urlInput, onPictureUrlChange, onImageRemoved]);

  const handleUrlCancel = useCallback(() => {
    setUrlInput(pictureUrl || '');
    setShowUrlInput(false);
  }, [pictureUrl]);

  const handleReset = useCallback(() => {
    setStatus('idle');
    setError(null);
  }, []);

  // Determine what to display
  const hasImage = pendingImage !== null || Boolean(pictureUrl);
  const displayUrl = previewUrl || pictureUrl;
  const isLoading = status === 'processing';
  const hasError = status === 'error';
  const isCropping = status === 'cropping';

  return (
    <div className="space-y-3">
      <Label>Image du produit</Label>

      {/* Image Preview or Upload Area */}
      <div className="relative">
        {hasImage && displayUrl && !isLoading ? (
          // Image preview
          <div className="relative rounded-lg overflow-hidden bg-gray-100 aspect-square max-w-[200px]">
            <img
              src={displayUrl}
              alt="Apercu du produit"
              className="w-full h-full object-cover"
            />
            {/* Delete button overlay */}
            {!disabled && (
              <button
                type="button"
                onClick={handleDelete}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                title="Supprimer l'image"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            {/* Indicator for pending upload */}
            {pendingImage && (
              <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                Prete
              </div>
            )}
          </div>
        ) : isLoading ? (
          // Loading state
          <div className="rounded-lg bg-gray-100 aspect-square max-w-[200px] flex flex-col items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 mb-2" />
            <span className="text-sm text-gray-500">Traitement...</span>
          </div>
        ) : hasError ? (
          // Error state
          <div className="rounded-lg bg-red-50 border border-red-200 aspect-square max-w-[200px] flex flex-col items-center justify-center p-4">
            <X className="h-8 w-8 text-red-400 mb-2" />
            <span className="text-sm text-red-600 text-center mb-3">{error}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
            >
              Annuler
            </Button>
          </div>
        ) : (
          // Empty state - upload area
          <div className={cn(
            "rounded-lg border-2 border-dashed border-gray-300 bg-gray-50",
            "aspect-square max-w-[200px] flex flex-col items-center justify-center p-4",
            disabled && "opacity-50 cursor-not-allowed"
          )}>
            <ImagePlus className="h-8 w-8 text-gray-400 mb-2" />
            <span className="text-sm text-gray-500 text-center">
              Ajoutez une photo
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!isLoading && !hasError && !disabled && (
        <div className="flex flex-wrap gap-2">
          {/* Camera button (mobile) */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            className="flex-1 min-w-[100px]"
          >
            <Camera className="h-4 w-4 mr-1" />
            Photo
          </Button>

          {/* Gallery button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 min-w-[100px]"
          >
            <ImagePlus className="h-4 w-4 mr-1" />
            Galerie
          </Button>

          {/* URL button */}
          {onPictureUrlChange && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowUrlInput(!showUrlInput)}
              className="px-2"
              title="Ajouter une URL"
            >
              <Link className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* URL Input */}
      {showUrlInput && onPictureUrlChange && (
        <div className="space-y-2 pt-2 border-t">
          <Label className="text-sm text-gray-600">URL de l'image</Label>
          <div className="flex gap-2">
            <Input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://..."
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              onClick={handleUrlSubmit}
              disabled={!urlInput.trim()}
            >
              OK
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleUrlCancel}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Crop modal */}
      {croppingImageUrl && isCropping && (
        <ImageCropper
          imageSrc={croppingImageUrl}
          isOpen={isCropping}
          onClose={handleCropCancel}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
        />
      )}
    </div>
  );
}
